import React, { useState, useEffect } from 'react'
import { format, toZonedTime } from 'date-fns-tz'
import { startCase } from 'lodash'
import ReactTooltip from 'react-tooltip'
import { loadStripe } from '@stripe/stripe-js'

import { capitalize } from '../../../helpers/helpers'
import { getAssetLink } from '../../../helpers/get-asset-link'
import StatusPill from '../../StatusPill/StatusPill'
import DropdownDots from '../../../components/DropdownDots/DropdownDots'
import ToggleWithText from '../../ToggleWithText/ToggleWithText'
import Icon from '../../Icons'
import PicturesModal from '../../Modal/PicturesModal'

import TextCell from './components/TextCell/TextCell'
import { Select } from '@/components/ui/select'

export const getGiftCardsTabs = (t) => [
  {
    id: 0,
    text: t('rewards-table:giftCardsTabs.all'),
    value: '',
  },
  {
    id: 1,
    text: t('rewards-table:giftCardsTabs.external'),
    value: 'external',
  },
  {
    id: 2,
    text: t('rewards-table:giftCardsTabs.customAmenity'),
    value: 'amenity',
  },
  {
    id: 3,
    text: t('rewards-table:giftCardsTabs.customServices'),
    value: 'service',
  },
]

export const getSettingsTabs = (t) => [
  {
    id: 0,
    text: t('rewards-table:settingsTabs.all'),
    value: 'all',
  },
  {
    id: 2,
    text: t('rewards-table:settingsTabs.amenity'),
    value: 'amenity',
  },
  {
    id: 3,
    text: t('rewards-table:settingsTabs.services'),
    value: 'service',
  },
  {
    id: 1,
    text: t('rewards-table:settingsTabs.external'),
    value: 'external',
  },
]

export const getGiftCardStatuses = (t) => [
  {
    id: 0,
    text: t('rewards-table:giftCardStatuses.all'),
    value: '',
  },
  {
    id: 1,
    text: t('rewards-table:giftCardStatuses.redeemed'),
    value: 'redeemed',
  },
  {
    id: 2,
    text: t('rewards-table:giftCardStatuses.notRedeemed'),
    value: 'not-redeemed',
  },
  {
    id: 3,
    text: t('rewards-table:giftCardStatuses.expired'),
    value: 'expired',
  },
]

export const getLoyaltyTabs = (t) => [
  {
    id: 0,
    text: t('rewards-loyalty:loyaltyTabs.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('rewards-loyalty:loyaltyTabs.pointsEarned'),
    value: 'points-earned',
  },
  {
    id: 2,
    text: t('rewards-loyalty:loyaltyTabs.pointsRedeemed'),
    value: 'reward-claimed',
  },
  {
    id: 3,
    text: t('rewards-loyalty:loyaltyTabs.pendingApproval'),
    value: 'pending-approval',
  },
]

export const getCsvFields = (timezone, t) => [
  {
    label: t('rewards-table:csvFields.sendDate'),
    value: (row) =>
      row.sendDate
        ? format(toZonedTime(row.sendDate, timezone), 'MM/dd/yyyy', {
            timeZone: timezone,
          })
        : '-',
  },
  {
    label: t('rewards-table:csvFields.sendTime'),
    value: (row) =>
      row.sendDate
        ? format(toZonedTime(row.sendDate, timezone), 'hh:mm a', {
            timeZone: timezone,
          })
        : '-',
  },
  {
    label: t('rewards-table:csvFields.source'),
    value: (row) => {
      if (row.source === 'loyalty') return t('rewards-table:csvFields.loyaltySystem')
      if (row.createdByUser) {
        return `${row.createdByUser.firstName} ${row.createdByUser.lastName} (${row.createdByUser.role})`
      }
      return '-'
    },
  },
  {
    label: t('rewards-table:csvFields.giftCardId'),
    value: 'id',
  },
  {
    label: t('rewards-table:csvFields.giftCardType'),
    value: (row) => capitalize(row.type),
  },
  {
    label: t('rewards-table:csvFields.giftCardName'),
    value: 'name',
  },
  {
    label: t('rewards-table:csvFields.value'),
    value: (row) => `$${row.amount}`,
  },
  {
    label: t('rewards-table:csvFields.category'),
    value: (row) =>
      giftCardCategoryOptions.find((option) => option.value === row.category)?.label ?? '-',
  },
  {
    label: t('rewards-table:csvFields.status'),
    value: (row) => startCase(row.status),
  },
  {
    label: t('rewards-table:csvFields.redeemDate'),
    value: (row) =>
      row.redeemDate
        ? format(toZonedTime(row.redeemDate, timezone), 'MM/dd/yyyy', {
            timeZone: timezone,
          })
        : '-',
  },
  {
    label: t('rewards-table:csvFields.redeemTime'),
    value: (row) =>
      row.redeemDate
        ? format(toZonedTime(row.redeemDate, timezone), 'hh:mm a', {
            timeZone: timezone,
          })
        : '-',
  },
  {
    label: t('rewards-table:csvFields.recipientName'),
    value: (row) => row.recipientName || '-',
  },
  {
    label: t('rewards-table:csvFields.recipientEmail'),
    value: (row) => row.recipientEmail || row.client?.email || '-',
  },
  {
    label: t('rewards-table:csvFields.recipientAddress'),
    value: (row) =>
      row.client
        ? `${row.client.street}${row.client.apartment ? ` #${row.client.apartment}` : ''}`
        : '-',
  },
  {
    label: t('rewards-table:csvFields.expirationDate'),
    value: (row) =>
      row.expirationDate
        ? format(toZonedTime(row.expirationDate, timezone), 'MM/dd/yyyy', {
            timeZone: timezone,
          })
        : '-',
  },
]

export const columns = ({ onShowDetails = () => {}, onSendEmail = () => {}, timezone, t }) => {
  return [
    {
      Header: t('rewards-gift:giftCardsColumns.sendDate'),
      accessor: 'sendDate',

      Cell: ({ value }) => (
        <TextCell
          title={format(toZonedTime(value, timezone), 'MM/dd/yyyy', {
            timeZone: timezone,
          })}
          text={format(toZonedTime(value, timezone), 'hh:mm a', { timeZone: timezone })}
        />
      ),
    },
    {
      Header: t('rewards-gift:giftCardsColumns.source'),

      Cell: ({ row: { original } }) => {
        const { createdByUser, source } = original
        if (source === 'loyalty') {
          return <TextCell text={t('rewards-table:csvFields.loyaltySystem')} />
        } else if (createdByUser) {
          return (
            <TextCell
              title={createdByUser.role}
              text={`${createdByUser.firstName} ${createdByUser.lastName}`}
            />
          )
        } else {
          return <TextCell text={'-'} />
        }
      },
    },
    {
      Header: t('rewards-gift:giftCardsColumns.giftCardId'),
      accessor: 'id',
    },
    {
      Header: t('rewards-gift:giftCardsColumns.giftCardTypeName'),

      Cell: ({ row: { original } }) => {
        const { type, name } = original
        return <TextCell title={capitalize(type)} text={name} />
      },
    },
    {
      Header: t('rewards-table:csvFields.category'),
      accessor: 'category',
      Cell: ({ value }) =>
        getGiftCardCategoryOptions(t).find((option) => option.value === value).label,
    },
    {
      Header: t('rewards-table:csvFields.value'),
      accessor: 'amount',
      Cell: ({ value }) => `$${value}`,
    },
    {
      Header: t('rewards-table:csvFields.status'),
      accessor: 'status',

      Cell: ({ value }) => <StatusPill title={startCase(value)} />,
    },
    {
      Header: t('rewards-table:csvFields.redeemDate'),
      accessor: 'redeemDate',

      Cell: ({ value }) =>
        value ? (
          <TextCell
            title={format(toZonedTime(value, timezone), 'MM/dd/yyyy', {
              timeZone: timezone,
            })}
            text={format(toZonedTime(value, timezone), 'hh:mm a', { timeZone: timezone })}
          />
        ) : (
          '-'
        ),
    },
    {
      Header: t('rewards-gift:giftCardsColumns.recipient'),

      Cell: ({ row: { original } }) => {
        const { recipientName, client } = original
        return (
          <TextCell
            title={recipientName}
            text={
              client ? `${client.street} ${client.apartment ? `#${client.apartment}` : ''}` : '-'
            }
          />
        )
      },
    },
    {
      Header: t('rewards-table:csvFields.expirationDate'),
      accessor: 'expirationDate',
      Cell: ({ value }) =>
        value ? format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone }) : '-',
    },
    {
      id: 'details_info',

      Cell: ({ row: { original } }) => (
        <DropdownDots
          options={[
            {
              id: 0,
              text: t('rewards-gift:giftCardsColumns.detailsOption'),
              action: () => onShowDetails(original),
            },
            ...(original.runaProductCode === 'VISA-US' || original.status === 'not-redeemed'
              ? [
                  {
                    id: 1,
                    text: t('rewards-gift:giftCardsColumns.sendEmailOption'),
                    action: () => onSendEmail(original.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ]
}

export const mapRecipientSummary = (
  { nonResident, recipientName, recipientEmail, resident },
  t,
) => [
  {
    title: t('rewards-gift:mapDetails.recipientName'),
    value: nonResident ? recipientName : resident.label,
  },
  {
    title: t('rewards-gift:mapDetails.unit'),
    value: nonResident ? null : resident.unit,
    hidden: nonResident,
  },
  {
    title: t('rewards-gift:mapDetails.recipientEmail'),
    value: nonResident ? recipientEmail : resident.email,
  },
]

export const mapGiftCardDetails = (
  giftCard,
  timezone,
  isAdminOrManager,
  t,
  canChangeStatus,
  statusOptions,
  giftCardStatus,
  onStatusChange,
) => {
  if (!giftCard) {
    return []
  }

  const { id, type, name, amount, status, expirationDate, description, runaOrderId, category } =
    giftCard
  return [
    {
      title: t('rewards-gift:mapDetails.giftCardId'),
      value: id,
    },
    {
      title: t('rewards-gift:mapDetails.giftCardType'),
      value: capitalize(type),
    },
    {
      title: t('rewards-gift:mapDetails.giftCardName'),
      value: name,
    },
    {
      title: t('rewards-table:csvFields.value'),
      value: `$${amount}`,
    },
    {
      title: t('rewards-table:csvFields.status'),
      value: canChangeStatus ? (
        <Select
          size="small"
          value={giftCardStatus}
          options={statusOptions}
          onChange={onStatusChange}
        />
      ) : (
        startCase(status)
      ),
    },
    {
      title: t('rewards-gift:mapDetails.runaOrderId'),
      value: runaOrderId || '-',
      copyAble: true,
      hidden: !isAdminOrManager,
    },
    {
      title: t('rewards-table:csvFields.expirationDate'),
      value:
        expirationDate &&
        format(toZonedTime(expirationDate, timezone), 'MM/dd/yyyy', { timeZone: timezone }),
      hidden: !expirationDate,
    },
    {
      title: t('rewards-gift:mapDetails.giftCardDescription'),
      value: description,
      hidden: !description,
    },
    {
      title: t('rewards-table:csvFields.category'),
      value: getGiftCardCategoryOptions(t).find((option) => option.value === category).label,
    },
  ]
}

export const mapTransactionDetails = (giftCard, timezone, t) => {
  if (!giftCard) {
    return []
  }

  const { sendDate, createdByUser, redeemDate } = giftCard

  return [
    {
      title: t('rewards-table:csvFields.sendDate'),
      value: format(toZonedTime(sendDate, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
    },
    {
      title: t('rewards-table:csvFields.source'),
      value: `${createdByUser.role} - ${createdByUser.firstName} ${createdByUser.lastName}`,
    },
    {
      title: t('rewards-table:csvFields.redeemDate'),
      value:
        redeemDate &&
        format(toZonedTime(redeemDate, timezone), 'MM/dd/yyyy hh:mm a', { timeZone: timezone }),
      hidden: !redeemDate,
    },
  ]
}

export const mapRecipientDetails = (giftCard, t) => {
  if (!giftCard) {
    return []
  }

  const { recipientName, recipientEmail } = giftCard

  return [
    {
      title: t('rewards-gift:mapDetails.recipient'),
      value: recipientName,
    },
    {
      title: t('rewards-gift:mapDetails.recipientAddress'),
      value: recipientEmail,
    },
  ]
}

export const getActivityText = (activity, timezone, t) => {
  const formattedDate = () =>
    format(toZonedTime(activity.updatedAt, timezone), 'MM/dd/yyyy hh:mm a', {
      timeZone: timezone,
    })

  switch (activity.status || activity.type) {
    case 'redeemed':
      return t('rewards-dashboard:activityItem.redeemedText', {
        name: activity.recipientName,
        date: formattedDate(),
      })
    case 'not-redeemed':
      return t('rewards-dashboard:activityItem.notRedeemedText', {
        name: activity.recipientName,
        date: formattedDate(),
      })
    case 'expired':
      return t('rewards-dashboard:activityItem.expiredText', {
        name: activity.recipientName,
        date: formattedDate(),
      })
    case 'reward-redeemed':
    case 'reward-claimed':
    case 'points-earned':
    case 'points-cancelled':
    case 'points-declined':
    case 'pending-approval':
      return activity.reason
  }
}

export const getActivityStatusIcon = (status) => {
  switch (status) {
    case 'not-redeemed':
      return (
        <Icon
          icon="cardNotRedeemed"
          className={`analytics-status-icon ${status}`}
          viewBox="-3 -4 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'redeemed':
      return (
        <Icon
          icon="cardRedeemed"
          className={`analytics-status-icon ${status}`}
          viewBox="-3 -4 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'expired':
      return (
        <Icon
          icon="cardExpired"
          className={`analytics-status-icon ${status}`}
          viewBox="-3 -3 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'reward-redeemed':
      return (
        <Icon
          icon="coins"
          className={`analytics-status-icon ${status}`}
          viewBox="-3 -3 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'reward-claimed':
      return <Icon icon="gift" className={`analytics-status-icon ${status}`} viewBox="0 0 25 25" />
    case 'points-earned':
      return (
        <Icon
          icon="handedCoins"
          className={`analytics-status-icon ${status}`}
          viewBox="-2 -3 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'points-cancelled':
    case 'points-declined':
      return (
        <Icon
          icon="crossedCoins"
          className={`analytics-status-icon ${status}`}
          viewBox="-3 -3 20 20"
          svgProps={{ fillRule: 'evenodd' }}
        />
      )
    case 'pending-approval':
      return <Icon icon="walk" className={`analytics-status-icon ${status}`} />
    default:
      break
  }
}

export const getGiftCardsAnalyticsMapping = (t) => [
  {
    name: 'totalSentValue',
    icon: (
      <Icon
        icon="cardNotRedeemed"
        className={`analytics-status-icon not-redeemed`}
        viewBox="-3 -4 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.valueSent'),
    isValue: true,
  },
  {
    name: 'totalRedeemedValue',
    icon: (
      <Icon
        icon="cardRedeemed"
        className={`analytics-status-icon redeemed`}
        viewBox="-3 -4 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.valueRedeemed'),
    isValue: true,
  },
  {
    name: 'totalUnredeemedValue',
    icon: (
      <Icon
        icon="cardExpired"
        className={`analytics-status-icon expired`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.valueUnredeemed'),
    isValue: true,
  },
  {
    name: 'totalSentCount',
    icon: (
      <Icon
        icon="cardNotRedeemed"
        className={`analytics-status-icon not-redeemed`}
        viewBox="-3 -4 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.sent'),
  },
  {
    name: 'totalRedeemedCount',
    icon: (
      <Icon
        icon="cardRedeemed"
        className={`analytics-status-icon redeemed`}
        viewBox="-3 -4 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.redeemed'),
  },
  {
    name: 'totalUnredeemedCount',
    icon: (
      <Icon
        icon="cardExpired"
        className={`analytics-status-icon expired`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.giftCards.unredeemed'),
  },
]

export const getLoyaltyAnalyticsMapping = (t) => [
  {
    name: 'valueEarned',
    icon: (
      <Icon
        icon="handedCoins"
        className={`analytics-status-icon earned`}
        viewBox="-2 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.valueEarned'),
  },
  {
    name: 'valueRedeemed',
    icon: (
      <Icon
        icon="coins"
        className={`analytics-status-icon redeemed`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.valueRedeemed'),
  },
  {
    name: 'valueUnredeemed',
    icon: (
      <Icon
        icon="crossedCoins"
        className={`analytics-status-icon expired`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.valueUnredeemed'),
  },
  {
    name: 'pointsEarned',
    icon: (
      <Icon
        icon="handedCoins"
        className={`analytics-status-icon earned`}
        viewBox="-2 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.pointsEarned'),
  },
  {
    name: 'pointsRedeemed',
    icon: (
      <Icon
        icon="coins"
        className={`analytics-status-icon redeemed`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.pointsRedeemed'),
  },
  {
    name: 'pointsUnredeemed',
    icon: (
      <Icon
        icon="crossedCoins"
        className={`analytics-status-icon expired`}
        viewBox="-3 -3 20 20"
        svgProps={{ fillRule: 'evenodd' }}
      />
    ),
    text: t('rewards-dashboard:analyticsMapping.loyalty.pointsUnredeemed'),
  },
]

export const leaderBoardColumns = ({
  onLoyaltyProfile = () => {},
  onRewardsHistory = () => {},
  t,
}) => {
  return [
    {
      Header: t('rewards-dashboard:leaderBoard.columns.ranking'),
      accessor: 'position',

      Cell: ({ value }) => <strong style={{ fontSize: 18 }}>{value}</strong>,
    },
    {
      Header: t('rewards-dashboard:leaderBoard.columns.resident'),

      Cell: ({ row: { original } }) => (
        <div className="resident-cell">
          <img src={getAssetLink({ path: 'chitown-avatar/avatar', file: original.avatar })} />
          <TextCell title={original.name} text={original.email} />
        </div>
      ),
    },
    {
      Header: t('rewards-dashboard:leaderBoard.columns.pointsEarned'),
      accessor: 'points',
    },
    {
      Header: t('rewards-dashboard:leaderBoard.columns.pointsDollarValue'),
      accessor: 'pointsDollarValue',

      Cell: ({ value }) => `$${value}`,
    },
    {
      Header: t('rewards-dashboard:leaderBoard.columns.dollarsRedeemed'),
      accessor: 'dollarsRedeemed',
      Cell: ({ value }) => `$${value}`,
    },
    {
      Header: t('rewards-dashboard:leaderBoard.columns.rewardsEarned'),
      accessor: 'rewardsRedeemed',
    },
    {
      id: 'details_info',

      Cell: ({ row: { original } }) => (
        <DropdownDots
          options={[
            {
              id: 0,
              text: t('rewards-dashboard:leaderBoard.columns.loyaltyProfileOption'),
              action: () => onLoyaltyProfile(original),
            },
            {
              id: 1,
              text: t('rewards-dashboard:leaderBoard.columns.rewardsHistoryOption'),
              action: () => onRewardsHistory(original),
            },
          ]}
        />
      ),
    },
  ]
}

export const rewardsHistoryColumns = ({ timezone, t }) => {
  return [
    {
      Header: t('rewards-loyalty:rewardsHistoryColumns.dateRewardEarned'),
      accessor: 'createdAt',

      Cell: ({ value }) =>
        value ? (
          <TextCell
            title={format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone })}
            text={format(toZonedTime(value, timezone), 'hh:mm a', { timeZone: timezone })}
          />
        ) : (
          '-'
        ),
    },
    {
      Header: t('rewards-loyalty:rewardsHistoryColumns.id'),
      accessor: 'id',
    },
    {
      Header: t('rewards-loyalty:rewardsHistoryColumns.rewardName'),
      accessor: 'name',
    },
    {
      Header: t('rewards-loyalty:rewardsHistoryColumns.type'),
      accessor: 'type',

      Cell: ({ value }) => `${startCase(value)} Gift Card`,
    },
    {
      Header: t('rewards-loyalty:rewardsHistoryColumns.valueDollar'),
      accessor: 'value',
      Cell: ({ value }) => `$${value}`,
    },
  ]
}

export const loyaltyProfileColumns = ({
  timezone,
  onReview = () => {},
  onDetails = () => {},
  onComplete = () => {},
  onDelete = () => {},
  t,
}) => {
  const statusText = (status) => {
    switch (status) {
      case 'pending':
        return t('rewards-loyalty:loyaltyProfileColumns.pendingApprovalStatus')
      default:
        return startCase(status)
    }
  }

  return [
    {
      Header: t('rewards-loyalty:loyaltyProfileColumns.activityName'),
      accessor: 'name',
    },
    {
      Header: t('rewards-loyalty:loyaltyProfileColumns.category'),
      accessor: 'type',

      Cell: ({ value }) => `${startCase(value)} Activity`,
    },
    {
      Header: (
        <span className="custom-col-name">
          {t('rewards-loyalty:manageActivitiesColumns.valuePoints')}{' '}
          <span>{t('rewards-loyalty:manageActivitiesColumns.pointsUnit')}</span>
        </span>
      ),
      accessor: 'points',
    },
    {
      Header: t('rewards-loyalty:loyaltyProfileColumns.completionDate'),
      accessor: 'date',

      Cell: ({ value }) =>
        value ? format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone }) : '-',
    },
    {
      Header: t('rewards-loyalty:loyaltyProfileColumns.status'),
      accessor: 'status',

      Cell: ({ value }) => <StatusPill title={statusText(value)} />,
    },
    {
      id: 'details_info',
      Header: t('rewards-loyalty:loyaltyProfileColumns.actions'),

      Cell: ({ row: { original } }) => {
        const { status, isPersonal } = original
        const options = []

        switch (status) {
          case 'pending':
            options.push({
              id: 0,
              text: t('rewards-loyalty:loyaltyProfileColumns.reviewOption'),
              action: () => onReview(original),
            })
            break
          case 'completed':
            options.push({
              id: 0,
              text: t('rewards-loyalty:loyaltyProfileColumns.detailsOption'),
              action: () => onDetails(original),
            })
            break
          case 'declined':
            options.push({
              id: 0,
              text: t('rewards-loyalty:loyaltyProfileColumns.detailsOption'),
              action: () => onDetails(original),
            })
            if (isPersonal) {
              options.push({
                id: 1,
                text: t('rewards-loyalty:loyaltyProfileColumns.deleteOption'),
                action: () => onDelete(original),
              })
            }
            break
          case 'not-started':
            options.push({
              id: 0,
              text: t('rewards-loyalty:loyaltyProfileColumns.completeOption'),
              action: () => onComplete(original),
            })
            if (isPersonal) {
              options.push({
                id: 1,
                text: t('rewards-loyalty:loyaltyProfileColumns.deleteOption'),
                action: () => onDelete(original),
              })
            }
            break
        }

        return <DropdownDots options={options} />
      },
    },
  ]
}

export const loyaltyTableColumns = ({
  timezone,
  onDecline = () => {},
  onApprove = () => {},
  onDetails = () => {},
  onLoyaltyProfile = () => {},
  onReview = () => {},
  t,
}) => {
  return [
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.date'),
      accessor: 'date',

      Cell: ({ value }) => (
        <TextCell
          title={format(toZonedTime(value, timezone), 'MM/dd/yyyy', { timeZone: timezone })}
          text={format(toZonedTime(value, timezone), 'hh:mm a', { timeZone: timezone })}
        />
      ),
    },
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.residentName'),

      Cell: ({ row: { original } }) => {
        const { name, address } = original
        return (
          <div className="text-cell">
            <span onClick={() => onLoyaltyProfile(original)} className="text-cell__clickable">
              {name}
            </span>
            <span>{address}</span>
          </div>
        )
      },
    },
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.activityDescription'),

      Cell: ({ row: { original } }) => {
        const { description, originalType, evidenceLink, evidenceText, evidenceImages } = original
        const noEvidences =
          !evidenceLink && !evidenceText && (!evidenceImages || !evidenceImages.length)
        const isReward = ['reward-redeemed', 'reward-claimed', 'reward-declined'].includes(
          originalType,
        )
        return (
          <div className="text-cell">
            <span>{description}</span>
            {!isReward && !noEvidences && (
              <span onClick={() => onReview(original)} className="text-cell__clickable">
                {t('rewards-loyalty:evidence.activityCompletionEvidence')}
              </span>
            )}
          </div>
        )
      },
    },
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.source'),
      accessor: 'source',
      Cell: ({ value }) => startCase(value),
    },
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.activityType'),
      accessor: 'displayType',

      Cell: ({ value }) => {
        let label = ''
        switch (value) {
          case 'points-cancelled':
            label = startCase('points-canceled')
            break
          default:
            label = startCase(value)
            break
        }
        return <StatusPill title={label} />
      },
    },
    {
      Header: t('rewards-loyalty:loyaltyTableColumns.approvedDeniedBy'),

      Cell: ({ row: { original } }) => {
        const {
          approvedDeclinedName,
          approvedDeclinedRole,
          cancelledName,
          cancelledRole,
          isAutoApproved,
          displayType,
        } = original

        if (
          [
            'points-earned',
            'points-declined',
            'points-cancelled',
            'reward-redeemed',
            'reward-declined',
          ].includes(displayType)
        ) {
          const isPointsCancelled = displayType === 'points-cancelled'
          return isAutoApproved ? (
            t('rewards-loyalty:loyaltyTableColumns.autoApproved')
          ) : (
            <TextCell
              title={isPointsCancelled ? cancelledRole : approvedDeclinedRole}
              text={isPointsCancelled ? cancelledName : approvedDeclinedName}
            />
          )
        }

        return '-'
      },
    },
    {
      id: 'details_info',
      Header: t('rewards-loyalty:loyaltyTableColumns.actions'),

      Cell: ({ row: { original } }) => {
        const { displayType } = original

        if (displayType === 'pending-approval') {
          return (
            <div className="cell-actions">
              <div
                data-tip
                data-for="decline-tip"
                className="cell-action-item decline"
                onClick={(e) => {
                  e.stopPropagation()
                  onDecline(original)
                }}
              >
                <Icon icon="cross" />
              </div>
              <div
                data-tip
                data-for="approve-tip"
                className="cell-action-item approve"
                onClick={(e) => {
                  e.stopPropagation()
                  onApprove(original)
                }}
              >
                <Icon icon="checkmark" viewBox="0 6 30 20" />
              </div>
            </div>
          )
        }

        const options = [
          {
            id: 0,
            text: t('rewards-loyalty:loyaltyTableColumns.detailsOption'),
            action: () => onDetails(original),
          },
        ]

        return <DropdownDots options={options} />
      },
    },
  ]
}

export const manageActivitiesColumns = ({
  timezone,
  onSwitch = () => {},
  onDetails = () => {},
  onEdit = () => {},
  onMakeFeatured = () => {},
  onMakeUnfeatured = () => {},
  onDelete = () => {},
  onActivate = () => {},
  t,
}) => {
  return [
    {
      Header: t('rewards-loyalty:manageActivitiesColumns.activityName'),
      accessor: 'name',

      Cell: ({ row: { original } }) => {
        const { name, expirationDate, isFeatured } = original
        return (
          <div className="activity-name">
            <span>{name}</span>
            {isFeatured && (
              <div className="is-featured">
                <Icon
                  styles={{ marginRight: expirationDate ? 4 : 0 }}
                  icon="gem"
                  viewBox={'0 0 40 40'}
                  svgProps={{
                    stroke: '#5857E7',
                    strokeWidth: '1.5',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                />
                {expirationDate && (
                  <div>
                    <span>{t('rewards-loyalty:manageActivitiesColumns.until')}</span>
                    <span>
                      {format(toZonedTime(expirationDate, timezone), 'MM/dd/yyyy', {
                        timeZone: timezone,
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      },
    },
    {
      Header: t('rewards-loyalty:manageActivitiesColumns.type'),
      accessor: 'type',

      Cell: ({ value }) => `${startCase(value)} Activity`,
    },
    {
      Header: (
        <span className="custom-col-name">
          {t('rewards-loyalty:manageActivitiesColumns.valuePoints')}{' '}
          <span>{t('rewards-loyalty:manageActivitiesColumns.pointsUnit')}</span>
        </span>
      ),
      accessor: 'points',
    },
    {
      Header: t('rewards-loyalty:manageActivitiesColumns.performance'),
      accessor: 'performance',
    },
    {
      id: 'activity_switch',
      Header: (
        <React.Fragment>
          {t('rewards-loyalty:manageActivitiesColumns.activeHeader')}
          <div data-tip data-for="pause-points-tip">
            <Icon icon="info" viewBox="0 0 25 25" />
            <ReactTooltip className="tooltip" id="pause-points-tip" multiline place="top">
              {t('rewards-loyalty:manageActivitiesColumns.activeTooltip')}
            </ReactTooltip>
          </div>
        </React.Fragment>
      ),

      Cell: ({ row: { original } }) => {
        const { id, isActive, isExpired, expirationDate } = original
        const [active, setActive] = useState(isActive)

        useEffect(() => {
          if (isActive !== active) {
            onSwitch(id, active)
          }
        }, [active])

        return expirationDate && isExpired ? (
          <span className="expired-label">
            {t('rewards-loyalty:manageActivitiesColumns.expiredLabel')}
          </span>
        ) : (
          <ToggleWithText
            text={
              active
                ? t('rewards-loyalty:manageActivitiesColumns.activeLabel')
                : t('rewards-loyalty:manageActivitiesColumns.inactiveLabel')
            }
            onChange={() => setActive(!active)}
            isChecked={active}
            color={'#548671'}
          />
        )
      },
    },
    {
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const { isExpired, isFeatured, isActive } = original

        const isCustomActivities = original.category === 'Custom Activities'

        const options = [
          {
            id: 0,
            text: t('rewards-loyalty:manageActivitiesColumns.detailsOption'),
            action: () => onDetails(original),
          },
        ]

        if (isExpired) {
          options.push({
            id: 4,
            text: t('rewards-loyalty:manageActivitiesColumns.activateOption'),
            action: () => onActivate(original),
          })
        } else {
          if (isCustomActivities) {
            options.push({
              id: 1,
              text: t('rewards-loyalty:manageActivitiesColumns.editParametersOption'),
              action: () => onEdit(original),
            })
          }

          if (isFeatured) {
            options.push({
              id: 2,
              text: t('rewards-loyalty:manageActivitiesColumns.unfeatureActivityOption'),
              action: () => onMakeUnfeatured(original),
            })
          } else if (isActive && !isFeatured) {
            options.push({
              id: 2,
              text: t('rewards-loyalty:manageActivitiesColumns.makeItFeaturedOption'),
              action: () => onMakeFeatured(original),
            })
          }
        }

        if (isCustomActivities) {
          options.push({
            id: 3,
            text: t('rewards-loyalty:manageActivitiesColumns.deleteOption'),
            action: () => onDelete(original),
          })
        }

        return <DropdownDots options={options} />
      },
    },
  ]
}

export const rewardsSettingsColumns = ({ onSwitch = () => {}, onDetails = () => {}, t }) => {
  return [
    {
      Header: t('rewards-table:rewardsSettingsColumns.rewardName'),
      accessor: 'name',
    },
    {
      Header: t('rewards-table:rewardsSettingsColumns.type'),
      accessor: 'type',
      Cell: ({ value }) => startCase(value),
    },
    {
      Header: t('rewards-table:rewardsSettingsColumns.performance'),
      accessor: 'performance',
    },
    {
      Header: (
        <span className="custom-col-name">
          {t('rewards-table:rewardsSettingsColumns.totalValueRedeemed')} <span>$</span>
        </span>
      ),
      accessor: 'totalValueRedeemed',
      Cell: ({ value }) => `$${value}`,
    },
    {
      id: 'reward_switch',
      Header: (
        <React.Fragment>
          {t('rewards-table:rewardsSettingsColumns.activeHeader')}
          <div data-tip data-for="switch-reward-tip">
            <Icon icon="info" viewBox="0 0 25 25" />
            <ReactTooltip className="tooltip" id="switch-reward-tip" multiline place="top">
              {t('rewards-table:rewardsSettingsColumns.activeRewardTooltip')}
            </ReactTooltip>
          </div>
        </React.Fragment>
      ),

      Cell: ({ row: { original } }) => {
        const { id, isActive, type } = original
        const [active, setActive] = useState(isActive)
        const isDisabled = type !== 'amenity'

        useEffect(() => {
          if (isActive !== active) {
            onSwitch(id, active)
          }
        }, [active])

        const tooltipText = isDisabled
          ? t('rewards-table:rewardsSettingsColumns.cannotBeDeactivatedTooltip')
          : t('rewards-table:rewardsSettingsColumns.activeRewardTooltip')

        return (
          <div data-tip data-for={`activity-active-tip-${id}`}>
            <ToggleWithText
              text={
                active
                  ? t('rewards-table:rewardsSettingsColumns.activeLabel')
                  : t('rewards-table:rewardsSettingsColumns.inactiveLabel')
              }
              onChange={() => setActive(!active)}
              isChecked={active}
              color={isDisabled ? '#5486714d' : '#548671'}
              isDisabled={isDisabled}
            />
            <ReactTooltip
              className="tooltip"
              id={`activity-active-tip-${id}`}
              multiline
              place="top"
            >
              {tooltipText}
            </ReactTooltip>
          </div>
        )
      },
    },
    {
      id: 'details_info',

      Cell: ({ row: { original } }) => {
        const options = [
          {
            id: 0,
            text: t('rewards-table:rewardsSettingsColumns.detailsOption'),
            action: () => onDetails(original),
          },
        ]

        return <DropdownDots options={options} />
      },
    },
  ]
}

export const REWARDS_AVAILABLE_FLAG = 'rewardsAvailable'

export const getCheckboxHandler = (handleFieldChange) => {
  return (event) => {
    const {
      target: { checked, name },
    } = event
    handleFieldChange({ target: { name, value: checked } })
  }
}

export const getNamedHandler = (handleFieldChange) => {
  return (value, fieldName) => handleFieldChange({ target: { name: fieldName, value } })
}

export const mapEvidenceInfo = ({ activityName, name, description }, t) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.resident'),
      value: name,
    },
    {
      title: t('rewards-loyalty:mapDetails.activity'),
      value: activityName,
    },
    {
      title: t('rewards-loyalty:mapDetails.activityDescription'),
      value: description && (
        <div
          className="ql-editor"
          style={{ padding: 0 }}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      ),
      newLine: true,
      hidden: !description,
    },
  ]
}

export const mapActivityBasicInformation = (
  { residentName, name, date, points, displayType },
  timezone,
  t,
) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.residentName'),
      value: residentName || name,
    },
    {
      title: t('rewards-loyalty:mapDetails.date'),
      value:
        date &&
        format(toZonedTime(date, timezone), 'MM/dd/yyyy hh:mm a', {
          timeZone: timezone,
        }),
      hidden: !date || displayType === 'points-cancelled',
    },
    {
      title: t('rewards-loyalty:mapDetails.pointsEarned'),
      value: points,
    },
  ]
}

export const mapRewardBasicInformation = (
  { name, date, rewardValue, displayType },
  timezone,
  t,
) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.residentName'),
      value: name,
    },
    {
      title: t('rewards-loyalty:mapDetails.redemptionDate'),
      value:
        date &&
        format(toZonedTime(date, timezone), 'MM/dd/yyyy hh:mm a', {
          timeZone: timezone,
        }),
      hidden: !date || displayType === 'reward-declined',
    },
    {
      title: t('rewards-loyalty:mapDetails.valueRedeemed'),
      value: `$${rewardValue}`,
    },
  ]
}

export const mapRewardDetails = ({ rewardName, rewardValue, rewardId, rewardType }, t) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.rewardName'),
      value: rewardName,
    },
    {
      title: t('rewards-loyalty:mapDetails.rewardType'),
      value: startCase(rewardType),
    },
    {
      title: t('rewards-loyalty:mapDetails.rewardValueDollar'),
      value: `$${rewardValue}`,
    },
    {
      title: t('rewards-loyalty:mapDetails.rewardId'),
      value: rewardId,
    },
  ]
}

export const mapClientActivityDetails = (
  {
    name,
    source,
    type,
    status,
    approvedOrDeclinedByUser,
    approvedDeclinedName,
    isAutoApproved,
    displayType,
    activityName,
  },
  t,
) => {
  const rows = [
    {
      title: t('rewards-loyalty:mapDetails.activityName'),
      value: activityName || name,
    },
    {
      title: t('rewards-loyalty:mapDetails.activityType'),
      value: `${startCase(type)} Activity`,
    },
    {
      title: t('rewards-loyalty:mapDetails.source'),
      value: startCase(source),
    },
  ]

  if (status === 'declined' || displayType === 'points-declined') {
    rows.push({
      title: t('rewards-loyalty:mapDetails.declinedBy'),
      value: approvedOrDeclinedByUser || approvedDeclinedName,
    })
  } else {
    rows.push({
      title: t('rewards-loyalty:mapDetails.approvedBy'),
      value: isAutoApproved
        ? t('rewards-loyalty:mapDetails.autoApproved')
        : approvedOrDeclinedByUser || approvedDeclinedName,
    })
  }
  return rows
}

export const mapActivityCanceledDetails = ({ cancelledName, date }, timezone, t) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.canceledBy'),
      value: cancelledName,
      hidden: !cancelledName,
    },
    {
      title: t('rewards-loyalty:mapDetails.date'),
      value: format(toZonedTime(date, timezone), 'MM/dd/yyyy hh:mm a', {
        timeZone: timezone,
      }),
    },
  ]
}

export const mapRewardDeclinedDetails = ({ approvedDeclinedName, date }, timezone, t) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.declinedBy'),
      value: approvedDeclinedName,
      hidden: !approvedDeclinedName,
    },
    {
      title: t('rewards-loyalty:mapDetails.date'),
      value: format(toZonedTime(date, timezone), 'MM/dd/yyyy hh:mm a', {
        timeZone: timezone,
      }),
    },
  ]
}

export const mapActivityDetails = (
  {
    name,
    type,
    points,
    performance,
    createdAt,
    expirationDate,
    category,
    description,
    usageLimitTimes,
  },
  timezone,
  t,
) => {
  return [
    {
      title: t('rewards-loyalty:mapDetails.activityName'),
      value: name,
    },
    {
      title: t('rewards-loyalty:mapDetails.type'),
      value: `${startCase(type)} Activity`,
    },
    {
      title: t('rewards-loyalty:mapDetails.usageLimit'),
      value: t('rewards-loyalty:mapDetails.usageLimitTimes', { count: usageLimitTimes }),
    },
    {
      title: t('rewards-loyalty:mapDetails.pointsValue'),
      value: points,
    },
    {
      title: t('rewards-loyalty:mapDetails.performance'),
      value: t('rewards-loyalty:mapDetails.timesCompleted', { count: performance }),
    },
    {
      title: t('rewards-loyalty:mapDetails.creationDateTime'),
      value: format(toZonedTime(createdAt, timezone), 'MM/dd/yyyy hh:mm a', {
        timeZone: timezone,
      }),
    },
    {
      title: t('rewards-loyalty:mapDetails.expirationDateTime'),
      value:
        expirationDate &&
        format(toZonedTime(expirationDate, timezone), 'MM/dd/yyyy hh:mm a', {
          timeZone: timezone,
        }),
      hidden: !expirationDate,
    },
    {
      title: t('rewards-loyalty:mapDetails.category'),
      value: category,
    },
    {
      title: t('rewards-loyalty:mapDetails.description'),
      value: description && (
        <div className="ql-editor" dangerouslySetInnerHTML={{ __html: description }} />
      ),
      hidden: !description,
      newLine: true,
    },
  ]
}

export const mapRewardSettingsDetails = ({ name, type, id, performance }, t) => {
  return [
    {
      title: t('rewards-table:rewardDetails.rewardName'),
      value: name,
    },
    {
      title: t('rewards-table:rewardDetails.type'),
      value: `${startCase(type)}`,
    },
    {
      title: t('rewards-table:rewardDetails.expirationPeriod'),
      value: t('rewards-table:rewardDetails.oneYear'),
    },
    {
      title: t('rewards-table:rewardDetails.rewardId'),
      value: id,
    },
    {
      title: t('rewards-table:rewardDetails.performance'),
      value: t('rewards-table:rewardDetails.timesRedeemed', { count: performance }),
    },
  ]
}

export const getActivityType = (t) => [
  {
    id: 0,
    text: t('rewards-loyalty:activityType.all'),
    value: 'all',
  },
  {
    id: 1,
    text: t('rewards-loyalty:manageActivitiesColumns.activeLabel'),
    value: 'active',
  },
  {
    id: 2,
    text: t('rewards-loyalty:manageActivitiesColumns.inactiveLabel'),
    value: 'inactive',
  },
]

export const toAmenityCardItem = ({ id, name, image, isUsed }) => ({
  id,
  name,
  isUsed,
  cardImageUrl: getAssetLink({ path: 'chitown-building', file: image }),
})

export const toServiceCardItem = ({ id, name, image }) => ({
  id,
  name,
  cardImageUrl: getAssetLink({ path: 'chitown-images/booking', file: image }),
})

export const renderEvidence = (
  activity,
  onOpenImageModal = () => {},
  photoIndexToDisplay = null,
  t,
) => {
  const { evidenceLink = null, evidenceText = null, evidenceImages = [] } = activity
  if (!evidenceLink && !evidenceText && (!evidenceImages || !evidenceImages.length)) {
    return (
      <div className="evidence-text">
        <span>{t('rewards-loyalty:evidence.none')}</span>
      </div>
    )
  }

  if (evidenceLink) {
    return (
      <div className="evidence-text">
        <span>{t('rewards-loyalty:evidence.link')}</span>
        <span>
          <a href={evidenceLink} rel="noreferrer" target="_blank">
            {evidenceLink}
          </a>
        </span>
      </div>
    )
  }

  if (evidenceText) {
    return (
      <div className="evidence-text">
        <span>{t('rewards-loyalty:evidence.activityApprovalText')}</span>
        <span>{evidenceText}</span>
      </div>
    )
  }

  if (evidenceImages.length) {
    return (
      <React.Fragment>
        <div className="evidence-images">
          {evidenceImages.map((name, index) => (
            <img
              key={name}
              className="image-gallery__image"
              src={getAssetLink({ path: 'loyalty_evidence', file: name })}
              onClick={() => onOpenImageModal(index)}
              alt={name}
            />
          ))}
        </div>
        <PicturesModal
          assetPath="loyalty_evidence"
          visible={photoIndexToDisplay !== null}
          pictures={evidenceImages}
          onCloseModal={() => onOpenImageModal(null)}
          indexToShow={photoIndexToDisplay}
        />
      </React.Fragment>
    )
  }
}

export const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY)

// kept English/static: `getCsvFields` above deliberately exports category names in English
// regardless of UI language (documented CSV-export limitation) — use
// `getGiftCardCategoryOptions(t)` wherever the `.label` is rendered to the current user
export const giftCardCategoryOptions = [
  { value: 'referral', label: 'Resident Referral' },
  { value: 'moveIn', label: 'Move-in Concession' },
  { value: 'renewal', label: 'Renewal Concession' },
  { value: 'maintenanceIssue', label: 'Maintenance Issue' },
  { value: 'other', label: 'Other' },
]

export const getGiftCardCategoryOptions = (t) => [
  { value: 'referral', label: t('rewards-table:giftCardCategoryOptions.residentReferral') },
  { value: 'moveIn', label: t('rewards-table:giftCardCategoryOptions.moveInConcession') },
  { value: 'renewal', label: t('rewards-table:giftCardCategoryOptions.renewalConcession') },
  {
    value: 'maintenanceIssue',
    label: t('rewards-table:giftCardCategoryOptions.maintenanceIssue'),
  },
  { value: 'other', label: t('rewards-table:giftCardCategoryOptions.other') },
]

export const getGiftCardCategoryFilterOptions = (t) => [
  { id: 0, text: t('rewards-table:giftCardCategoryFilterOptions.allCategories'), value: '' },
  ...getGiftCardCategoryOptions(t).map((category, index) => ({
    id: index + 1,
    text: category.label,
    value: category.value,
  })),
]
